// src/lib/http/bffClient.ts
// Tiny, shareable HTTP client for talking to the BFF from browser or SSR/Node.

export interface BffErrorBody { [k: string]: any }

export class BffError extends Error {
  status: number;
  body: BffErrorBody | string | undefined;
  constructor(message: string, status: number, body?: BffErrorBody | string) {
    super(message);
    this.name = 'BffError';
    this.status = status;
    this.body = body;
  }
}

/** Resolve the BFF origin in browser, Vite, CRA, or Node. */
function resolveOrigin(): string {
  const IS_BROWSER = typeof window !== 'undefined';
  const w: any = IS_BROWSER ? window : undefined;
  const ENV: any = (typeof globalThis !== 'undefined' && (globalThis as any).process && (globalThis as any).process.env) || {};

  const fromWindow =
    (IS_BROWSER && (w?.__BFF_ORIGIN__ ?? w?.BFF_ORIGIN)) || undefined;

  const fromCRA = ENV.REACT_APP_BFF || undefined;

  const fromNode = (!IS_BROWSER && (ENV.BFF_ORIGIN || ENV.REACT_APP_BFF || ENV.VITE_BFF_ORIGIN)) || undefined;

  // Dev fallback: if the SPA runs on 3000, assume BFF on 8080
  const devDefault =
    IS_BROWSER && window.location.port === '3000' ? 'http://localhost:8080' : undefined;

  return (
    fromWindow ||
    fromCRA ||
    fromNode ||
    devDefault ||
    (IS_BROWSER ? window.location.origin : 'http://localhost:8080')
  );
}

type AuthHeaderProvider = () => Record<string, string> | null | undefined;

export type ClientOptions = {
  origin?: string;               // e.g. http://localhost:8080
  basePath?: string;             // e.g. /bff  or  /bff/ttlock/api
  timeoutMs?: number;            // default 12_000
  defaultHeaders?: Record<string, string>;
  getAuthHeaders?: AuthHeaderProvider; // optional; cookie-first by default
};

type Queryish = Record<string, string | number | boolean | undefined>;

function joinPath(a: string, b: string) {
  return `${a.replace(/\/+$/, '')}/${b.replace(/^\/+/, '')}`;
}

// Proper timeout helper that passes an AbortSignal into your fetch
async function withTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  ms: number
): Promise<T> {
  if (!ms || ms <= 0) return fn(new AbortController().signal);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fn(ctrl.signal);
  } finally {
    clearTimeout(timer);
  }
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.split('; ').find(c => c.startsWith(`${name}=`));
  return m ? decodeURIComponent(m.split('=').slice(1).join('=')) : null;
}

export function createBffClient(opts: ClientOptions = {}) {
  const ORIGIN   = opts.origin ?? resolveOrigin();
  const BASEPATH = opts.basePath ?? '/bff';
  const TIMEOUT  = opts.timeoutMs ?? 12_000;
  const DEF_HDRS = opts.defaultHeaders ?? {};
  let getAuth    = opts.getAuthHeaders ?? null;

  function makeUrl(path: string, qs?: Queryish) {
    const url = new URL(joinPath(ORIGIN, joinPath(BASEPATH, path)));
    if (qs) for (const [k, v] of Object.entries(qs)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
    return url;
  }

  async function request<T>(
    path: string,
    init?: (RequestInit & { qs?: Queryish; autoRefresh?: boolean; timeoutMs?: number })
  ): Promise<T> {
    const url = makeUrl(path, init?.qs);
    const method = (init?.method || 'GET').toUpperCase();

    // Build headers with the real Headers class to satisfy TS/Fetch
    const headers = new Headers({ ...DEF_HDRS, ...(init?.headers as Record<string, string> | undefined) });

    // Optional auth headers (mostly unnecessary when using cookies; still supported)
    if (getAuth) {
      const extra = getAuth();
      if (extra) for (const [k, v] of Object.entries(extra)) headers.set(k, v);
    }

    // Set Accept if absent
    if (!headers.has('accept')) headers.set('accept', 'application/json');

    // Content-Type convenience (but never for FormData)
    const hasBody   = init && 'body' in init && init.body != null;
    const isForm    = typeof FormData !== 'undefined' && (init?.body instanceof FormData);
    if (hasBody && !isForm && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }

    // CSRF on unsafe methods (if your BFF sets a csrf cookie)
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrf = getCookie('csrf');
      if (csrf && !headers.has('x-csrf')) headers.set('x-csrf', csrf);
    }

    const doFetch = async (signal: AbortSignal) => {
      const res = await fetch(url.toString(), {
        ...init,
        method,
        headers,
        signal,
        credentials: 'include', // send cookies to BFF
      });

      // Handle 401 → try one silent refresh (unless this *is* the refresh)
      const auto = init?.autoRefresh ?? true;
      const isAuthRoute = url.pathname.endsWith('/auth/login') || url.pathname.endsWith('/auth/refresh');
      if (res.status === 401 && auto && !isAuthRoute) {
        // try refresh then retry once
        const refreshUrl = makeUrl('/auth/refresh');
        // Include CSRF header if cookie is present (many BFFs require it for POST)
        const refreshHeaders = new Headers();
        const csrf = getCookie('csrf');
        if (csrf) refreshHeaders.set('x-csrf', csrf);
        refreshHeaders.set('accept', 'application/json');
        await fetch(refreshUrl.toString(), {
          method: 'POST',
          credentials: 'include',
          headers: refreshHeaders,
        }).catch(() => {});
        const retry = await fetch(url.toString(), {
          ...init,
          method,
          headers,
          signal,
          credentials: 'include',
        });
        return retry;   
      }

      return res;
    };

  const effTimeout = init?.timeoutMs != null ? init.timeoutMs : TIMEOUT;
  const res = await withTimeout(doFetch, effTimeout);
    const text = await res.text().catch(() => '');

    // Try to parse JSON when possible
    const tryJson = () => {
      try { return text ? JSON.parse(text) : undefined; }
      catch { return undefined; }
    };

    if (!res.ok) {
      const body = tryJson() ?? text;
      // Prefer a useful message from body if present
      const msg =
        (typeof body === 'object' && body && ('detail' in body || 'message' in body || 'error' in body))
          ? String((body as any).detail ?? (body as any).message ?? (body as any).error)
          : `${method} ${url.pathname} → ${res.status} ${res.statusText}`;
      throw new BffError(msg, res.status, body);
    }

    return (tryJson() ?? ({} as any)) as T;
  }

  const get   = <T>(p: string, qs?: Queryish) => request<T>(p, { method: 'GET', qs });
  const del   = <T>(p: string) => request<T>(p, { method: 'DELETE' });
  const post  = <T, B = unknown>(p: string, body?: B, opts?: { timeoutMs?: number }) =>
    request<T>(p, {
      method: 'POST',
      timeoutMs: opts?.timeoutMs,
      body: body && !(body as any instanceof FormData) ? JSON.stringify(body) : (body as any)
    });
  const patch = <T, B = unknown>(p: string, body?: B, opts?: { timeoutMs?: number }) =>
    request<T>(p, {
      method: 'PATCH',
      timeoutMs: opts?.timeoutMs,
      body: body && !(body as any instanceof FormData) ? JSON.stringify(body) : (body as any)
    });

  return {
    origin  : ORIGIN,
    basePath: BASEPATH,
    setAuthProvider(fn: AuthHeaderProvider) { getAuth = fn; },
    request, get, del, post, patch,
  };
}
